import type {
  DefaultResponseBody,
  ListResponseBody,
  PlaylistVisibility,
  UserPlaylist,
  UserPlaylistItem,
  UserPlaylistView,
} from "@gerama/types";
import type { ApiClient } from "../client";

/** A public share-link playlist — `GET /user-playlists/shared/:token`, no auth (product plan section 5.9). */
export function createCoreUserPlaylistsApi(client: ApiClient) {
  return {
    bySharedToken: (token: string, signal?: AbortSignal) =>
      client
        .request<DefaultResponseBody<UserPlaylist>>(`/user-playlists/shared/${token}`, { signal })
        .then((res) => res.data as UserPlaylist),

    /** Public items of a share-link playlist — published songs/videos only. */
    sharedItems: (token: string, signal?: AbortSignal) =>
      client
        .request<{ data?: { list: UserPlaylistItem[]; count: number } }>(
          `/user-playlists/shared/${token}/items`,
          { signal },
        )
        .then((res) => res.data ?? { list: [], count: 0 }),

    /** The signed-in user's own playlists. */
    mine: (params: { page?: number; limit?: number } = {}, signal?: AbortSignal) =>
      client
        .request<ListResponseBody<UserPlaylist>>("/user-playlists", {
          query: { ...params },
          signal,
        })
        .then((res) => res.data ?? { list: [], count: 0 }),

    create: (name: string, visibility: "private" | "public" = "private") =>
      client
        .request<DefaultResponseBody<UserPlaylist>>("/user-playlists", {
          method: "POST",
          body: { name, visibility },
        })
        .then((res) => res.data as UserPlaylist),

    /** A playlist by its public code — the owner sees any visibility, others public or link-shared only. */
    byCode: (code: string, signal?: AbortSignal) =>
      client
        .request<DefaultResponseBody<UserPlaylistView>>(`/user-playlists/c/${code}`, { signal })
        .then((res) => res.data as UserPlaylistView),

    /** Its items — every item for the owner, published ones for everyone else. */
    itemsByCode: (code: string, signal?: AbortSignal) =>
      client
        .request<{ data?: { list: UserPlaylistItem[]; count: number } }>(
          `/user-playlists/c/${code}/items`,
          { signal },
        )
        .then((res) => res.data ?? { list: [], count: 0 }),

    follow: (code: string) =>
      client
        .request<DefaultResponseBody<{ isFollowing: boolean; followerCount: number }>>(
          `/user-playlists/c/${code}/follow`,
          { method: "POST" },
        )
        .then((res) => res.data),

    unfollow: (code: string) =>
      client
        .request<DefaultResponseBody<{ isFollowing: boolean; followerCount: number }>>(
          `/user-playlists/c/${code}/follow`,
          { method: "DELETE" },
        )
        .then((res) => res.data),

    /** Other people's playlists the signed-in user follows. */
    followed: (params: { page?: number; limit?: number } = {}, signal?: AbortSignal) =>
      client
        .request<ListResponseBody<UserPlaylist>>("/user-playlists/followed", {
          query: { ...params },
          signal,
        })
        .then((res) => res.data ?? { list: [], count: 0 }),

    /** Rename, describe or change visibility; `shared_link` mints a share token. */
    update: (
      id: number,
      input: { name?: string; description?: string | null; visibility?: PlaylistVisibility },
    ) =>
      client
        .request<DefaultResponseBody<UserPlaylist>>(`/user-playlists/${id}`, {
          method: "PATCH",
          body: input,
        })
        .then((res) => res.data as UserPlaylist),

    remove: (id: number) =>
      client.request<DefaultResponseBody<unknown>>(`/user-playlists/${id}`, { method: "DELETE" }),

    removeItem: (id: number, itemType: "song" | "video", itemId: number) =>
      client.request<DefaultResponseBody<unknown>>(
        `/user-playlists/${id}/items/${itemType}/${itemId}`,
        { method: "DELETE" },
      ),

    /** Another user's public playlists (their profile). */
    byUser: (
      userId: number,
      params: { page?: number; limit?: number } = {},
      signal?: AbortSignal,
    ) =>
      client
        .request<ListResponseBody<UserPlaylist>>(`/user-playlists/by-user/${userId}`, {
          query: { ...params },
          signal,
        })
        .then((res) => res.data ?? { list: [], count: 0 }),

    addItem: (id: number, itemType: "song" | "video", itemId: number) =>
      client.request<DefaultResponseBody<unknown>>(`/user-playlists/${id}/items`, {
        method: "POST",
        body: { itemType, itemId },
      }),
  };
}
